import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { useSWRConfig } from "swr";
import type { NskResponse } from "~/models/Api";
import type { DealReportImage } from "~/models/DealReportImage";
import { operableReportImage } from "~/models/DealReportImage";
import { getReportImageSwrKey } from "~/utils/DealReportImageUtil";
import { ReportImageInput } from "./ReportImageInput";
import Styles from "./ReportImagesAreaBlockRemarks.module.scss";

type Props = {
	companyType: "company" | "vendor";
	handleUpdate: ((submitValue: DealReportImage) => Promise<NskResponse>) | null;
	reportImage: DealReportImage;
};

const renderView = (content: string | null | undefined): ReactNode => (
	<div className={Styles.ContentView}>
		{content === null || content === "" ? (
			<span className={Styles.YetInput}>未入力</span>
		) : (
			<span>{content}</span>
		)}
	</div>
);

export function ReportImagesAreaBlockRemarks({
	reportImage,
	handleUpdate,
	companyType
}: Props) {
	const router = useRouter();
	const { mutate: globalMutate } = useSWRConfig();
	const isVendor = companyType === "vendor";
	const operableImage = operableReportImage(reportImage, companyType);

	const handleMutateReportImages = async (): Promise<void> => {
		await globalMutate(getReportImageSwrKey(router.asPath, reportImage.dealId));
	};

	const submitPoint = async (submitValue: string): Promise<void> => {
		const newReportImage = { ...reportImage, point: submitValue };
		await handleUpdate?.(newReportImage);
		await handleMutateReportImages();
	};
	const submitNote = async (submitValue: string): Promise<void> => {
		const newReportImage = { ...reportImage, note: submitValue };
		await handleUpdate?.(newReportImage);
		await handleMutateReportImages();
	};
	const isOutputPdfImage = reportImage.outputPdfImage;
	const isOutputPdfNote = reportImage.outputPdfNote;
	return (
		<div className={Styles.Remarks}>
			{reportImage.construction === "targeted" && (
				<div className={Styles.RemarkRow}>
					{reportImage.type === "DealInspectionReportImage" ? (
						<div className={Styles.ConstructionState}>施工推奨箇所</div>
					) : (
						<div className={Styles.ConstructionState}>施工対象箇所</div>
					)}
				</div>
			)}
			<div className={Styles.RemarkRow}>
				<div className={Styles.Label}>撮影場所</div>
				<div className={Styles.Content}>
					{operableImage ? (
						<ReportImageInput
							handleSubmit={submitPoint}
							inputType="point"
							value={reportImage.point ?? ""}
							viewComponent={renderView(reportImage.point)}
						/>
					) : (
						<>{renderView(reportImage.point)}</>
					)}
				</div>
			</div>
			<div className={Styles.RemarkRow}>
				<div className={Styles.Label}>備考</div>
				<div className={Styles.Content}>
					{operableImage ? (
						<ReportImageInput
							handleSubmit={submitNote}
							inputType="note"
							value={reportImage.note ?? ""}
							viewComponent={renderView(reportImage.note)}
						/>
					) : (
						<>{renderView(reportImage.note)}</>
					)}
					{!isVendor && isOutputPdfImage && !isOutputPdfNote ? (
						<p className={Styles.NotOutPutPdfNote}>
							備考は報告書に出力されません
						</p>
					) : null}
				</div>
			</div>
		</div>
	);
}
